import { FileInfo, IdEither } from '@cognite/sdk';
import { PNID_DATASET_ID, PNID_MIME_TYPE, PNID_SOURCE } from 'config/apiConfig';
import { getClient } from '../config/cognitesdk';

export class FilesResourceService {
  fetchFilesByIds(ids: number[]): Promise<FileInfo[]> {
    const filesToGet: IdEither[] = ids.map((id) => {
      return { id };
    });
    return getClient().files.retrieve(filesToGet);
  }

  fetchFilesByName(fileName: string): Promise<FileInfo[]> {
    return getClient()
      .files.list({
        filter: {
          mimeType: PNID_MIME_TYPE,
          source: PNID_SOURCE,
          name: fileName,
          dataSetIds: PNID_DATASET_ID.map((externalId) => ({ externalId })),
          metadata: { contextualized: 'T' },
        },
        limit: 1000,
      })
      .autoPagingToArray({ limit: -1 });
  }

  fetchFileById(id: number): Promise<FileInfo> {
    return this.fetchFilesByIds([id]).then((files) => files[0]);
  }

  searchFilesByAssetIds(assetIds: number[]): Promise<FileInfo[]> {
    return getClient().files.search({
      filter: {
        mimeType: PNID_MIME_TYPE,
        source: PNID_SOURCE,
        dataSetIds: PNID_DATASET_ID.map((externalId) => ({ externalId })),
        assetIds,
        metadata: { contextualized: 'T' },
      },
    });
  }

  searchFilesByName(name: string): Promise<FileInfo[]> {
    return getClient().files.search({
      filter: {
        mimeType: PNID_MIME_TYPE,
        dataSetIds: PNID_DATASET_ID.map((externalId) => ({ externalId })),
        source: PNID_SOURCE,
        metadata: { contextualized: 'T' },
      },
      search: { name },
      limit: 15,
    });
  }

  listPnIDsforAsset(assetId: number): Promise<FileInfo[]> {
    return getClient()
      .files.list({
        filter: {
          mimeType: PNID_MIME_TYPE,
          source: PNID_SOURCE,
          assetIds: [assetId],
          dataSetIds: PNID_DATASET_ID.map((externalId) => ({ externalId })),
          metadata: { contextualized: 'T' },
        },
        limit: 1000,
      })
      .autoPagingToArray({ limit: -1 });
  }

  getRevision(file: FileInfo): number {
    const revString = file.metadata?.revision ?? file.metadata?.REVISION;
    if (!revString) {
      throw new Error(`Missing revision metadata on file ${file.name}`);
    }
    const rev = parseInt(revString, 10);
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(rev)) {
      throw new Error(
        `Invalid revision value "${revString}" on file ${file.name}`
      );
    }
    return rev;
  }

  getLatestPnIDFiles(allPnIDs: FileInfo[]): FileInfo[] {
    const map = new Map<string, FileInfo>();

    // eslint-disable-next-line no-restricted-syntax
    for (const file of allPnIDs) {
      const key = file.name;
      const rev = this.getRevision(file);
      const existing = map.get(key);

      if (
        !existing ||
        rev > this.getRevision(existing) ||
        (rev === this.getRevision(existing) &&
          new Date(file.createdTime) > new Date(existing.createdTime))
      ) {
        map.set(key, file);
      }
    }
    return Array.from(map.values());
  }

  getFileDownloadUrl(fileId: number) {
    return getClient().files.getDownloadUrls([{ id: fileId }]);
  }

  checkForDuplicatedPnids(array: FileInfo[]) {
    const highestRevision = Math.max(
      ...array.map((item) =>
        Number(item.metadata?.revision || item.metadata?.REVISION)
      )
    );
    const highestRevisionPnids = array.filter(
      (item) =>
        Number(item.metadata?.REVISION || item.metadata?.revision) ===
        highestRevision
    );

    return highestRevisionPnids.length > 1;
  }

  getIdOfLatestFile(fileName: string): Promise<number> {
    return this.fetchFilesByName(fileName).then((filesResponse) => {
      if (!filesResponse || !filesResponse.length) {
        return 0;
      }

      if (this.checkForDuplicatedPnids(filesResponse)) {
        return 0;
      }

      const sortedResponse = filesResponse.sort(
        // if revision is different, use that, if same, use created date
        (a, b) => {
          const revisionA = a.metadata?.REVISION || a.metadata?.revision || 0;

          const revisionB = b.metadata?.REVISION || b.metadata?.revision || 0;
          return (
            Number(revisionB) - Number(revisionA) ||
            +b.createdTime - +a.createdTime
          );
        }
      );
      return sortedResponse[0].id;
    });
  }
}
